/**
 * Ghost Cursor Debug Tool
 * Pins a player's hover state on a button so it persists after pointer leave.
 * Used for multi-tab testing of majority vote / co-sign systems.
 *
 * Press `[` while hovering a button to pin (renders a semi-transparent cursor).
 * Press `]` to clear.
 */

import { create } from 'zustand';

// ==================== STORE ====================

export const ghostCursorDebugEnabled = import.meta.env.DEV;

interface GhostCursorState {
  /** The button ID the ghost is pinned to, or null */
  ghostButtonId: string | null;
  /** Screen X where the ghost was placed */
  ghostX: number;
  /** Screen Y where the ghost was placed */
  ghostY: number;
}

export const useGhostCursorStore = create<GhostCursorState>()(() => ({
  ghostButtonId: null,
  ghostX: 0,
  ghostY: 0,
}));

// ==================== INTERNAL STATE ====================

// Cleanup function is kept outside the store (not serializable)
let ghostCleanup: (() => void) | null = null;

// Unique owner token — identifies which hook/element instance set the ghost.
// Prevents a different hook with the same buttonId from accidentally clearing it.
let ghostOwnerToken: symbol | null = null;

// Track current mouse position so we can snapshot it on pin
let lastMouseX = 0;
let lastMouseY = 0;

// Attach mouse listener eagerly so position is available on first pin
if (ghostCursorDebugEnabled && typeof window !== 'undefined') {
  window.addEventListener('mousemove', (e) => {
    lastMouseX = e.clientX;
    lastMouseY = e.clientY;
  }, { passive: true });
}

// ==================== PUBLIC API ====================

/** Returns the currently pinned button ID, or null if no ghost is active. */
export function getGhostButtonId(): string | null {
  return useGhostCursorStore.getState().ghostButtonId;
}

/**
 * Pin the ghost cursor on a button. The cleanup function sends the leave event.
 * Returns an owner token that the caller must use with `clearGhostCursorIfOwner`
 * to prevent other hooks/elements with the same buttonId from accidentally clearing it.
 */
export function setGhostCursor(buttonId: string, cleanupFn: () => void): symbol {
  if (!ghostCursorDebugEnabled) {
    return Symbol('ghost-owner-disabled');
  }
  // Clear any existing ghost first
  if (ghostCleanup && getGhostButtonId() !== buttonId) {
    ghostCleanup();
  }
  ghostCleanup = cleanupFn;
  const token = Symbol('ghost-owner');
  ghostOwnerToken = token;
  useGhostCursorStore.setState({
    ghostButtonId: buttonId,
    ghostX: lastMouseX,
    ghostY: lastMouseY,
  });
  console.log('[GhostCursor] Pinned on', buttonId, 'at', lastMouseX, lastMouseY);
  return token;
}

/** Clear the ghost cursor and send the pending leave event. */
export function clearGhostCursor(): void {
  if (!ghostCursorDebugEnabled) {
    return;
  }
  if (ghostCleanup) {
    ghostCleanup();
    ghostCleanup = null;
  }
  ghostOwnerToken = null;
  const old = useGhostCursorStore.getState().ghostButtonId;
  useGhostCursorStore.setState({ ghostButtonId: null, ghostX: 0, ghostY: 0 });
  if (old) {
    console.log('[GhostCursor] Cleared from', old);
  }
}

/**
 * Clear the ghost cursor only if the caller owns it (via token from setGhostCursor).
 * Returns true if cleared, false if the caller doesn't own this ghost.
 */
export function clearGhostCursorIfOwner(token: symbol): boolean {
  if (ghostOwnerToken !== token) return false;
  clearGhostCursor();
  return true;
}
